import {
  Bell,
  ChevronDown,
  ClipboardList,
  Contact,
  CreditCard,
  DollarSign,
  FileSignature,
  FileText,
  FolderOpen,
  HelpCircle,
  ChevronsLeft,
  ChevronsRight,
  LayoutDashboard,
  Mail,
  Megaphone,
  Package,
  PhoneCall,
  ScrollText,
  Settings,
  Store,
  Users,
  UsersRound,
  ChevronsUpDown,
  LogOut,
  Moon,
  Sun,
  User,
  type LucideIcon,
} from "lucide-react";
import { useState } from "react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import Image from "@/lib/compat/next-image";
import { usePathname, useRouter } from "@/lib/navigation";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Sidebar,
  SidebarBrand,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarItem,
  SidebarSeparator,
  SidebarTrigger,
  useSidebar,
} from "@myela/ui";
import { IconTooltip } from "@/components/ui/icon-tooltip";
import { UserAvatar } from "@/components/common/UserAvatar";
import { useAuth } from "@/hooks/useAuth";
import { useTheme } from "@/components/theme-provider";
import { toast } from "@/hooks/use-toast";
import { AGENT_STATEMENTS_ENABLED, canReadCommissions } from "@/lib/commissionAccess";
import { BRAND, productLabel } from "@/lib/brand";
import { HIDDEN_NAV } from "@/lib/navHidden";
import { leaveDemoUrl } from "@/lib/dashboard";
import { DemoTag } from "@/components/layout/DemoMode";

interface NavItem {
  href: string;
  label: string;
  icon: LucideIcon;
  matchPrefix?: boolean;
  extraActivePaths?: string[];
}

// A collapsible parent that reveals its child NavItems (e.g. Mailroom).
interface NavGroup {
  label: string;
  icon: LucideIcon;
  /** Path prefix used to tell whether any child is active. */
  match: string;
  items: NavItem[];
}

function isNavGroup(entry: NavItem | NavGroup): entry is NavGroup {
  return "items" in entry;
}

function isActive(pathname: string, item: NavItem) {
  const matchesPrefix = (base: string) =>
    base === "/"
      ? pathname === "/"
      : pathname === base || pathname.startsWith(base + "/");

  if (item.matchPrefix ? matchesPrefix(item.href) : pathname === item.href) {
    return true;
  }
  return item.extraActivePaths?.some(matchesPrefix) ?? false;
}

// Collapsed rail: shrink each item to a centered 40px circle — the default
// keeps its horizontal padding and label gap, leaving the icon off-center in
// a square-cornered pill. (data-state only exists on the desktop <aside>.)
const collapsedItemClasses =
  "group-data-[state=collapsed]/sidebar:mx-auto group-data-[state=collapsed]/sidebar:size-10 group-data-[state=collapsed]/sidebar:justify-center group-data-[state=collapsed]/sidebar:gap-0 group-data-[state=collapsed]/sidebar:rounded-full group-data-[state=collapsed]/sidebar:p-0";

function NavItemLink({
  item,
  pathname,
  sub,
}: {
  item: NavItem;
  pathname: string;
  sub?: boolean;
}) {
  const Icon = item.icon;
  const router = useRouter();
  const { isDesktop, setOpen } = useSidebar();
  const active = isActive(pathname, item);
  return (
    <SidebarItem
      href={item.href}
      onClick={(e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0)
          return;
        e.preventDefault();
        // On mobile/tablet the sidebar is a Sheet — close it when navigating.
        if (!isDesktop) setOpen(false);
        router.push(item.href);
      }}
      icon={<Icon className="size-4" />}
      active={active}
      tooltip={item.label}
      // Inactive rows sit a touch lighter than the active item so the active
      // (primary) row carries the emphasis.
      className={cn(
        collapsedItemClasses,
        !active && "text-sidebar-foreground/70",
        sub && active && "!bg-[var(--ink-100)] !text-[var(--ink-900)] !font-medium",
      )}
    >
      {item.label}
    </SidebarItem>
  );
}

/** Collapsible parent item that expands to show its child links. */
function NavGroupLink({ group, pathname }: { group: NavGroup; pathname: string }) {
  const Icon = group.icon;
  const { open: sidebarOpen, isDesktop } = useSidebar();
  const collapsed = isDesktop && !sidebarOpen;
  const anyActive =
    pathname === group.match || pathname.startsWith(group.match + "/");
  const [open, setOpen] = useState(anyActive);

  // Collapsed (icon-only) rail: a nested list looks broken, so render the group
  // as a single icon that links to its base route. Sub-items return when open.
  if (collapsed) {
    return (
      <NavItemLink
        item={{ href: group.match, label: group.label, icon: group.icon, matchPrefix: true }}
        pathname={pathname}
      />
    );
  }

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger asChild>
        {/* Mirrors SidebarItem's styling so it matches the flat nav rows, but
            lays out as: icon · label (flex-1) · chevron (far right). */}
        <button
          type="button"
          title={group.label}
          className={cn(
            "relative flex w-full items-center gap-2.5 rounded-xl px-3 py-2 text-sm",
            "transition-colors",
            !anyActive && "text-sidebar-foreground/70",
            "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring",
            anyActive &&
              "bg-primary text-primary-foreground font-medium hover:bg-primary/90 hover:text-primary-foreground",
          )}
        >
          <span className="flex size-4 shrink-0 items-center justify-center">
            <Icon className="size-4" />
          </span>
          {!collapsed && (
            <>
              <span className="flex-1 truncate text-left">{group.label}</span>
              <ChevronDown
                className={cn(
                  "size-3.5 shrink-0 opacity-70 transition-transform",
                  open && "rotate-180",
                )}
              />
            </>
          )}
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="ml-3.5 mt-0.5 flex flex-col gap-0.5 border-l border-[var(--ink-150)] pl-2">
          {group.items.map((item) => (
            <NavItemLink key={item.href} item={item} pathname={pathname} sub />
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

// Sidebar-footer account menu: avatar + name/email that opens a dropdown with
// theme toggle, account, notifications, and sign out (moved off the old navbar).
function SidebarUser() {
  const router = useRouter();
  const { user, signOut } = useAuth();
  const { theme, toggleTheme } = useTheme();

  const first = user?.firstName ?? undefined;
  const last = user?.lastName ?? undefined;
  const displayName = [first, last].filter(Boolean).join(" ") || undefined;
  const fullName =
    displayName ?? (user?.email ? user.email.split("@")[0] : "User");
  const email = user?.email ?? "";

  const handleSignOut = async () => {
    try {
      await signOut();
      router.push("/login");
      toast.success("Successfully signed out");
    } catch {
      toast.error("Failed to sign out");
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="flex w-full items-center gap-2 rounded-xl px-2 py-1.5 text-left transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring data-[state=open]:bg-sidebar-accent"
        >
          <UserAvatar name={displayName} email={email} className="shrink-0" />
          <div className="grid min-w-0 flex-1 leading-tight group-data-[state=collapsed]/sidebar:hidden">
            <span className="truncate text-sm font-medium text-foreground">
              {fullName}
            </span>
            <span className="truncate text-xs text-muted-foreground">
              {email}
            </span>
          </div>
          <ChevronsUpDown className="ml-auto size-4 shrink-0 text-muted-foreground group-data-[state=collapsed]/sidebar:hidden" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="right" align="end" sideOffset={8} className="w-56">
        <DropdownMenuLabel className="p-0 font-normal">
          <div className="flex items-center gap-2 px-1 py-1.5">
            <UserAvatar name={displayName} email={email} className="shrink-0" />
            <div className="grid min-w-0 flex-1 leading-tight">
              <span className="truncate text-sm font-medium">{fullName}</span>
              <span className="truncate text-xs text-muted-foreground">
                {email}
              </span>
            </div>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => router.push("/account")}>
          <User className="mr-2 size-4" />
          Account
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => router.push("/notifications")}>
          <Bell className="mr-2 size-4" />
          Notifications
        </DropdownMenuItem>
        {/* Keep the menu open on toggle so the theme change is visible. */}
        <DropdownMenuItem
          onSelect={(e) => {
            e.preventDefault();
            toggleTheme();
          }}
        >
          {theme === "light" ? (
            <Moon className="mr-2 size-4" />
          ) : (
            <Sun className="mr-2 size-4" />
          )}
          {theme === "light" ? "Dark mode" : "Light mode"}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleSignOut}>
          <LogOut className="mr-2 size-4" />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function PartnerSidebar() {
  const pathname = usePathname();
  const { isDesktop, open: sidebarOpen } = useSidebar();
  const { user } = useAuth();

  // While impersonating, the sidebar must reflect what the IMPERSONATED user
  // sees — not the admin's INTERNAL view. `user.impersonating` carries the
  // target's userType; fall back to the real user when not impersonating.
  // Sourced entirely from the API (useAuth) — no legacy Supabase profile.
  const effectiveUser = user?.impersonating ?? user;
  const effectiveEmail = effectiveUser?.email;
  const effectiveUserType = effectiveUser?.userType;
  const isInternalUser = effectiveUserType === "INTERNAL";
  const isGodaddyMerchant = effectiveUserType === "GODADDY_MERCHANT";

  // Header: logo/name slide out on collapse (SidebarBrand); trigger stays.
  const brandHeader = (
    <SidebarHeader className="border-b-0">
      <SidebarBrand>
        <Image
          src={BRAND.assets.logo}
          alt={BRAND.assets.logoAlt}
          width={32}
          height={32}
          className="size-8 shrink-0 rounded-[9px]"
          priority
        />
        <div className="flex min-w-0 flex-col leading-tight">
          <span className="truncate text-sm font-bold text-foreground">
            {BRAND.handle} {BRAND.tagline}
          </span>
          <span className="flex min-w-0 items-center gap-2">
            <span className="truncate text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
              Agent Portal
            </span>
            {user?.demoOrg && <DemoTag />}
          </span>
          {user?.demoOrg && (
            <span className="text-xs text-muted-foreground">
              You're in Myela Demo ·{" "}
              <a href={leaveDemoUrl()} className="font-medium text-foreground underline underline-offset-4">
                Leave demo
              </a>
            </span>
          )}
        </div>
      </SidebarBrand>
      {/* Mobile's Sheet has its own close button — the fold trigger is desktop-only.
          Lucide icons override the library's built-in Remix ones (CLAUDE.md §11). */}
      {isDesktop && (
        <IconTooltip
          label={sidebarOpen ? "Collapse sidebar" : "Expand sidebar"}
          side="right"
        >
          <SidebarTrigger
            aria-label={sidebarOpen ? "Collapse sidebar" : "Expand sidebar"}
            className="ml-auto shrink-0"
            openIcon={<ChevronsLeft className="size-4" />}
            closedIcon={<ChevronsRight className="size-4" />}
          />
        </IconTooltip>
      )}
    </SidebarHeader>
  );

  // GoDaddy merchant: restricted view (Poynt Collect + Helpdesk only)
  if (isGodaddyMerchant) {
    const items: NavItem[] = [
      { href: "/poynt-collect", label: "Poynt Collect", icon: CreditCard },
      {
        href: "/helpdesk",
        label: "Helpdesk",
        icon: HelpCircle,
        matchPrefix: true,
      },
    ];
    return (
      <Sidebar>
        {brandHeader}
        <SidebarContent>
          <SidebarGroup>
            {items.map((item) => (
              <NavItemLink key={item.href} item={item} pathname={pathname} />
            ))}
          </SidebarGroup>
        </SidebarContent>
        <SidebarFooter className="border-t-0">
          <SidebarUser />
        </SidebarFooter>
      </Sidebar>
    );
  }

  const mainNav: NavItem[] = [
    { href: "/", label: "Dashboard", icon: LayoutDashboard, matchPrefix: true },
    {
      href: "/applications",
      label: isInternalUser ? "Applications" : "My Applications",
      icon: FileText,
      matchPrefix: true,
      extraActivePaths: ["/new-application"],
    },
    {
      href: "/merchants",
      label: isInternalUser ? "Merchants" : "My Merchants",
      icon: Store,
      matchPrefix: true,
    },
    // Agents only — sub-agents get no commissions area; internal users use
    // the admin workspace under Internal Tools.
    ...(effectiveUserType === "AGENT" && AGENT_STATEMENTS_ENABLED
      ? [{ href: "/commissions", label: "My Commissions", icon: DollarSign, matchPrefix: true }]
      : []),
    { href: "/products", label: productLabel("Products"), icon: Package },
    {
      href: "/documents",
      label: "Documents",
      icon: FolderOpen,
      matchPrefix: true,
    },
    // { href: "/gateway-wiki", label: "Gateway Wiki", icon: BookOpen },
    ...(isInternalUser
      ? [{ href: "/poynt-collect", label: "Poynt Collect", icon: CreditCard }]
      : []),

    {
      href: "/helpdesk",
      label: "Helpdesk",
      icon: HelpCircle,
      matchPrefix: true,
    },
    {
      href: "/notifications",
      label: "Notifications",
      icon: Bell,
      matchPrefix: true,
      extraActivePaths: ["/alerts"],
    },
    {
      href: "/settings",
      label: "Settings",
      icon: Settings,
      matchPrefix: true,
      extraActivePaths: ["/account"],
    },
    // Sub-agent management is INTERNAL-only — agents request sub-agents from
    // internal staff. /admin/users is route-guarded (PermissionDenied) for them.
  ];

  // Roles aren't carried on the impersonation payload, so while impersonating
  // we treat the effective user as having none (admin-gated items stay hidden).
  const userRoles: string[] = user?.impersonating ? [] : (user?.roles ?? []);
  const isAdmin = userRoles.includes("admin");

  const adminNav: (NavItem | NavGroup)[] = isInternalUser
    ? [
        // Forms — builder, live preview, submissions, secure links, and
        // campaign embeds.
        {
          href: "/internal-tools/forms",
          label: "Forms",
          icon: ClipboardList,
          matchPrefix: true,
        },
        // Announcements — "what's new" dialog content + role targeting.
        // Lives under /admin (INTERNAL-only route guard) but shown in the
        // Internal Tools sidebar group.
        {
          href: "/admin/announcements",
          label: "Announcements",
          icon: Megaphone,
          matchPrefix: true,
        },
        // AI call logs (Retell) — moved here from the main nav.
        {
          href: "/ai-call-log",
          label: "AI Call Log",
          icon: PhoneCall,
        },
        // DocuSeal document templates — create/edit e-signature templates and map
        // fields via the embedded form builder.
        {
          href: "/admin/document-templates",
          label: "DocuSeal Templates",
          icon: FileSignature,
          matchPrefix: true,
        },
        // Mailroom — collapsible group: templates/send, contacts, groups, logs.
        {
          label: "Mailroom",
          icon: Mail,
          match: "/mailroom",
          items: [
            { href: "/mailroom", label: "Templates", icon: FileText },
            { href: "/mailroom/contacts", label: "Contacts", icon: Contact, matchPrefix: true },
            { href: "/mailroom/groups", label: "Groups", icon: UsersRound, matchPrefix: true },
            { href: "/mailroom/logs", label: "Logs", icon: ScrollText, matchPrefix: true },
          ],
        },
        ...(isAdmin
          ? [
              {
                href: "/admin/users",
                label: "Users",
                icon: Users,
              },
            ]
          : []),
        // Commissions is role-gated (admin and finance only), not on the email
        // allowlist. We're already inside the INTERNAL-only branch.
        ...(canReadCommissions(userRoles)
          ? [
              {
                href: "/admin/commissions",
                label: "Commissions",
                icon: DollarSign,
                matchPrefix: true,
              },
            ]
          : []),
      ]
    : [];

  return (
    <Sidebar>
      {brandHeader}
      <SidebarContent>
        <SidebarGroup>
          {mainNav.filter((item) => !HIDDEN_NAV.has(item.href)).map((item) => (
            <NavItemLink key={item.href} item={item} pathname={pathname} />
          ))}
        </SidebarGroup>

        {adminNav.length > 0 && (
          <>
            {/* The "Internal Tools" group label divides the sections when the
                sidebar is expanded, but it's hidden on the collapsed rail — so
                show a separator there instead to keep the two groups distinct. */}
            <SidebarSeparator className="hidden group-data-[state=collapsed]/sidebar:block" />
            <SidebarGroup className="mt-1">
              {/* Fully remove the label from the flex flow on the icon rail —
                  the library only zeroes its height (max-height:0), so it would
                  otherwise still take a `gap-1` slot above the first icon. */}
              <SidebarGroupLabel className="mb-1.5 group-data-[state=collapsed]/sidebar:hidden">
                Internal Tools
              </SidebarGroupLabel>
              {adminNav.map((item) =>
                isNavGroup(item) ? (
                  <NavGroupLink key={item.label} group={item} pathname={pathname} />
                ) : (
                  <NavItemLink key={item.href} item={item} pathname={pathname} />
                ),
              )}
            </SidebarGroup>
          </>
        )}
      </SidebarContent>
      <SidebarFooter className="border-t-0">
        <SidebarUser />
      </SidebarFooter>
    </Sidebar>
  );
}
